import { useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { FuseHit, ModalsWrapperContext } from '@kanda-libs/ks-design-library';

import useSearchQuery from 'hooks/useSearchQuery';
import { selectors } from 'store';

import { SEARCH_OPTIONS, USER_MODAL_ID } from './constants';
import { User, extractUserArray } from './helpers';

export interface ModalProps {
  email?: string;
  cid?: string;
}

export interface UsersListPageHook {
  isLoading: boolean;
  query: string;
  hits: FuseHit<User>[];
  setQuery: (query: string) => void;
  modalProps: ModalProps;
  clearModalProps: () => void;
  onRowClick: (props: ModalProps) => void;
}

export default function useUsersListPage(): UsersListPageHook {
  const isLoading = useSelector(selectors.getIsLoading);
  const companies = useSelector(selectors.company.getEntitiesAsArray);
  const [modalProps, setModalProps] = useState<ModalProps>({
    email: undefined,
    cid: undefined,
  });

  const { showModal } = useContext(ModalsWrapperContext);

  const users: User[] = useMemo(
    () => extractUserArray(isLoading, companies),
    [isLoading, companies],
  );

  const { query, setQuery, hits } = useSearchQuery(
    users,
    'users',
    SEARCH_OPTIONS,
  );

  const onRowClick = useCallback(
    ({ email, cid }: ModalProps) => {
      if (!email || !cid) {
        console.log(
          `Error - missing: ${[
            !email ? 'email' : undefined,
            !cid ? 'cid' : undefined,
          ]
            .filter(Boolean)
            .join(', ')}`,
        );
        return;
      }
      setModalProps({
        email,
        cid,
      });
      showModal(USER_MODAL_ID);
    },
    [showModal],
  );

  const clearModalProps = useCallback(
    () => setModalProps({ email: undefined, cid: undefined }),
    [],
  );

  return {
    isLoading,
    hits,
    query,
    setQuery,
    modalProps,
    clearModalProps,
    onRowClick,
  };
}
